import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface SubcategoryLabel {
  key: string;
  nameEn: string;
  nameZh: string;
  sortOrder: number;
}

/**
 * Canonical subcategory labels from `service_subcategories`.
 * `therapy_wiki.subcategories` stores keys only — this is the single place
 * that turns a key into a human label, in either language.
 */
export const useSubcategoryLabels = () => {
  return useQuery({
    queryKey: ['service-subcategory-labels'],
    queryFn: async (): Promise<Record<string, SubcategoryLabel>> => {
      const { data, error } = await supabase
        .from('service_subcategories')
        .select('subcategory_key, name_en, name_zh, sort_order')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      const map: Record<string, SubcategoryLabel> = {};
      (data || []).forEach((row) => {
        map[row.subcategory_key] = {
          key: row.subcategory_key,
          nameEn: row.name_en,
          nameZh: row.name_zh,
          sortOrder: row.sort_order ?? 0,
        };
      });
      return map;
    },
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

/** Localized labels for a therapy's subcategory keys, in canonical order. */
export const localizeSubcategories = (
  isZh: boolean,
  keys: string[] | null | undefined,
  labels: Record<string, SubcategoryLabel> | undefined
): string[] => {
  if (!keys?.length) return [];
  return keys
    .map((k) => labels?.[k])
    .filter((l): l is SubcategoryLabel => !!l)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((l) => (isZh ? l.nameZh : l.nameEn));
};
